import React from "react";
import { FormGroup, Label, Input } from "reactstrap";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Numeric from "../components/Numeric/Numeric";
import { parseDate } from "./dateUtils";

export function FormField({
  label,
  changedColor = false,
  value,
  onChange = () => {},
  name,
  disabled = false,
  type = "text",
  error,
  description,
  decimalScale,
  dataTypeDecimalName,
  rows,
  placeholder,
  thousandSeparator
}) {
  if (type === "date") {
    return (
      <DateField
        label={label}
        value={value}
        onChange={onChange}
        name={name}
        onBlur={onChange}
        disabled={disabled}
        error={error}
        description={description}
      />
    );
  }
  if (type === "number") {
    let dataTypeDecimalName = "default";
    if (label) {
      label.toLowerCase() === "price" || "currency" || "percentage" || "quantity"
        ? (dataTypeDecimalName = label.toLowerCase())
        : null;
    }
    return (
      <FormGroup>
        <Label style={{ fontWeight: 500, fontSize: "15px", display: "block" }}>{label}</Label>
        <Numeric
          disabled={disabled}
          value={value || ''}
          onChange={(e) => onChange(name, e)}
          name={name}
          placeholder={placeholder}
          decimalScale={decimalScale}
          dataTypeDecimalName={dataTypeDecimalName}
          thousandSeparator={thousandSeparator}
        />
        <div className="invalid-feedback" style={{ display: error ? "block" : "none" }}>
          {error}
        </div>
        {!error && description && (
          <div className="valid-feedback" style={{ display: "block", color: "gray" }}>
            {description}
          </div>
        )}
      </FormGroup>
    );
  }
  return (
    <FormGroup>
      <Label
        style={{
          fontWeight: 500,
          fontSize: "15px",
          display: "block",
          color: changedColor ? "#36a9e1" : "black",
        }}
      >
        {label}
      </Label>
      <Input
        type={type}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        rows={rows}
        placeholder={placeholder}
      />
      <div className="invalid-feedback" style={{ display: error ? "block" : "none" }}>
        {error}
      </div>
      {!error && description && (
        <div className="valid-feedback" style={{ display: "block", color: "gray" }}>
          {description}
        </div>
      )}
    </FormGroup>
  );
}

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  error: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  changedColor: PropTypes.bool,
  placeholder: PropTypes.string,
};

FormField.defaultProps = {
  disabled: false,
  onChange: () => {},
  type: "text",
  error: undefined,
  description: undefined,
  changedColor: false,
  placeholder: "",
};

export function DateField({
  label,
  changedColor = false,
  value,
  onChange = () => {},
  name,
  disabled = false,
  error,
  description,
}) {
  let showDate = value && value !== "0000-00-00" ? new Date(value) : undefined;

  // prevent ui crash
  if (!(showDate instanceof Date) || isNaN(showDate)) {
    showDate = new Date();
  }

  return (
    <FormGroup>
      {label && (
        <Label
          style={{
            fontWeight: 500,
            fontSize: "15px",
            display: "block",
            color: changedColor ? "#36a9e1" : "black",
          }}
        >
          {label}
        </Label>
      )}
      <DatePicker
        selected={showDate}
        onChange={(date) => onChange(name, parseDate(date))}
        disabled={disabled}
        style={{ zIndex: 9999 }}
        dateFormat="dd/MM/yyyy"
        customInput={<Input />}
      />
      <div className="invalid-feedback" style={{ display: error ? "block" : "none" }}>
        {error}
      </div>
      {!error && description && (
        <div className="valid-feedback" style={{ display: "block", color: "gray" }}>
          {description}
        </div>
      )}
    </FormGroup>
  );
}

DateField.propTypes = {
  label: PropTypes.any.isRequired,
  changedColor: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  error: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
};

DateField.defaultProps = {
  disabled: false,
  changedColor: false,
  onChange: () => {},
  error: undefined,
  description: undefined,
};

export function SelectFormField({
  label,
  changedColor = false,
  value,
  name,
  disabled = false,
  onChange = () => {},
  selectItems,
  getSelectItemId,
  getSelectItemName,
  error,
  description,
}) {
  const getValueFromId = (id) => {
    for (let i = 0; i < selectItems.length; i += 1) {
      if (String(getSelectItemId(selectItems[i])) === String(id)) {
        return getSelectItemName(selectItems[i]);
      }
    }
    return "";
  };

  return (
    <FormGroup>
      <Label
        style={{
          fontWeight: 500,
          fontSize: "15px",
          display: "block",
          color: changedColor ? "#36a9e1" : "black",
        }}
      >
        {label}
      </Label>
      <Input
        type="select"
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(name, e.target.value, getValueFromId(e.target.value))}
      >
        {value == "" || value == undefined || value == 0 ? <option value="" selected hidden></option> : null}
        {selectItems.map((s) => (
          <option key={getSelectItemId(s)} value={getSelectItemId(s)} selected={getSelectItemId(s) === value}>
            {getSelectItemName(s)}
          </option>
        ))}
      </Input>
      <div className="invalid-feedback" style={{ display: error ? "block" : "none" }}>
        {error}
      </div>
      {!error && description && (
        <div className="valid-feedback" style={{ display: "block", color: "gray" }}>
          {description}
        </div>
      )}
    </FormGroup>
  );
}

SelectFormField.propTypes = {
  label: PropTypes.string.isRequired,
  changedColor: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  error: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  selectItems: PropTypes.array.isRequired,
  getSelectItemId: PropTypes.func.isRequired,
  getSelectItemName: PropTypes.func.isRequired,
};

SelectFormField.defaultProps = {
  onChange: () => {},
  changedColor: false,
  disabled: false,
  error: undefined,
  description: undefined,
};

export function CheckFormField({
  label,
  changedColor,
  initialValue,
  value,
  name,
  disabled = false,
  onChange = () => {},
  error,
  description,
}) {
  return (
    <FormGroup>
      <Label
        style={{
          fontWeight: 500,
          fontSize: "15px",
          display: "block",
          color: changedColor ? "#36a9e1" : "black",
        }}
      >
        {label}
        <Input
          type="checkbox"
          disabled={disabled}
          style={{ marginLeft: 10 }}
          defaultChecked={initialValue}
          value={value}
          onChange={(e) => onChange(name, e.target.checked === "true" || e.target.checked === true)}
        />
      </Label>
      <div className="invalid-feedback" style={{ display: error ? "block" : "none" }}>
        {error}
      </div>
      {!error && description && (
        <div className="valid-feedback" style={{ display: "block", color: "gray" }}>
          {description}
        </div>
      )}
    </FormGroup>
  );
}

CheckFormField.propTypes = {
  label: PropTypes.string.isRequired,
  initialValue: PropTypes.bool.isRequired,
  changedColor: PropTypes.bool,
  value: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  error: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

CheckFormField.defaultProps = {
  disabled: false,
  changedColor: false,
  onChange: () => {},
  error: undefined,
  description: undefined,
};

export function CustomFormInput({ label, changedColor = false, children, error, description }) {
  return (
    <FormGroup>
      <Label
        style={{
          fontWeight: 500,
          fontSize: "15px",
          display: "block",
          color: changedColor ? "#36a9e1" : "black",
        }}
      >
        {label}
      </Label>
      {children}
      <div className="invalid-feedback" style={{ display: error ? "block" : "none" }}>
        {error}
      </div>
      {!error && description && (
        <div className="valid-feedback" style={{ display: "block", color: "gray" }}>
          {description}
        </div>
      )}
    </FormGroup>
  );
}

CustomFormInput.propTypes = {
  label: PropTypes.string.isRequired,
  changedColor: PropTypes.bool,
  children: PropTypes.node.isRequired,
  error: PropTypes.string,
  description: PropTypes.string,
};

CustomFormInput.defaultProps = {
  error: undefined,
  changedColor: false,
  description: undefined,
};

export function MonthDateField({
  label,
  changedColor = false,
  value,
  onChange = () => {},
  name,
  disabled = false,
  error,
  description,
  minDate = undefined,
}) {
  const showDate = value && value !== "0000-00-00" ? new Date(value) : undefined;
  return (
    <FormGroup>
      <Label
        style={{
          fontWeight: 500,
          fontSize: "15px",
          display: "block",
          color: changedColor ? "#36a9e1" : "black",
        }}
      >
        {label}
      </Label>
      <DatePicker
        selected={showDate}
        onChange={(date) => onChange(name, date.toISOString())}
        disabled={disabled}
        minDate={minDate}
        dateFormat="MM/yyyy"
        showMonthYearPicker
        customInput={<Input />}
      />
      <div className="invalid-feedback" style={{ display: error ? "block" : "none" }}>
        {error}
      </div>
      {!error && description && (
        <div className="valid-feedback" style={{ display: "block", color: "gray" }}>
          {description}
        </div>
      )}
    </FormGroup>
  );
}

MonthDateField.propTypes = {
  label: PropTypes.any.isRequired,
  changedColor: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  error: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  minDate: PropTypes.object,
};

MonthDateField.defaultProps = {
  disabled: false,
  changedColor: false,
  onChange: () => {},
  error: undefined,
  description: undefined,
  minDate: undefined,
};

export function DateFieldStyle({
  label,
  changedColor = false,
  value,
  onChange = () => {},
  name,
  disabled = false,
  error,
  description,
}) {
  const showDate = value && value !== "0000-00-00" ? new Date(value) : undefined;
  return (
    <DatePicker
      selected={showDate}
      onChange={(date) => onChange(name, date.toISOString())}
      disabled={disabled}
      dateFormat="dd/MM/yyyy"
      customInput={<Input />}
    />
  );
}

DateFieldStyle.propTypes = {
  label: PropTypes.any.isRequired,
  changedColor: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  error: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
};

DateFieldStyle.defaultProps = {
  disabled: false,
  changedColor: false,
  onChange: () => {},
  error: undefined,
  description: undefined,
};

